import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ArrowLeft, ArrowRight, CalendarCheck } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useProfessionalAvailability, useProfessionalMutations } from '@/hooks/useProfessionalsApi'
import { WeeklyHoursPanel } from '@/components/team/AvailabilityEditor'
import type { AvailabilityWindowInput } from '@/lib/api'

type ProfessionalAvailabilityStepProps = {
  onNext: () => void
  onBack: () => void
  onSkip: () => void
  onRefresh: () => Promise<void>
}

export function ProfessionalAvailabilityStep({
  onNext,
  onBack,
  onSkip,
  onRefresh,
}: ProfessionalAvailabilityStepProps) {
  const { t } = useTranslation('onboarding')
  const { user } = useAuth()
  const availability = useProfessionalAvailability(user?.id ?? null)
  const mutations = useProfessionalMutations()

  const handleSave = async (payload: AvailabilityWindowInput[]) => {
    if (!user) return
    if (payload.length === 0) {
      toast.error(t('professional.availability.validation.windowRequired'))
      return
    }

    try {
      await mutations.setAvailabilityWindows(user.id, payload)
      toast.success(t('professional.availability.saved'))
      await Promise.all([availability.refetch(), onRefresh()])
      onNext()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : t('professional.availability.error'))
    }
  }

  return (
    <div className="flex flex-col px-4 py-6 animate-in fade-in duration-500">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F08A24] to-[#f9a84c] mb-4">
          <CalendarCheck weight="bold" className="w-7 h-7 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-[#0B2A4A] dark:text-white mb-2">
          {t('professional.availability.title')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          {t('professional.availability.subtitle')}
        </p>
      </div>

      <div className="max-w-2xl mx-auto w-full">
        <Card>
          <CardContent className="p-6">
            {availability.isLoading ? (
              <div className="h-40 w-full animate-pulse rounded-lg bg-muted" />
            ) : (
              <WeeklyHoursPanel
                initialWindows={availability.data?.windows ?? []}
                isSaving={mutations.isLoading}
                onSave={handleSave}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center justify-between mt-10 max-w-2xl mx-auto w-full">
        <Button
          onClick={onBack}
          variant="ghost"
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
        >
          <ArrowLeft weight="bold" className="mr-2 w-4 h-4" />
          {t('back')}
        </Button>

        <div className="flex items-center gap-3">
          <Button
            onClick={onSkip}
            variant="ghost"
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500"
          >
            {t('skipStep')}
          </Button>
          <Button
            onClick={onNext}
            variant="outline"
            className="border-[#F08A24]/40 text-[#0B2A4A] dark:text-white"
          >
            {t('continue')}
            <ArrowRight weight="bold" className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
